import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import { plural } from '../../../../../../../shared/helpers/plural';
import { LICENSING_ISSUE_TYPES, QUALITY_ISSUE_TYPES, VULN_ISSUE_TYPES } from '../../../../../../../shared/issuesV2';
import { getSeverityFromCVSS, vulnSeverityMappingToString } from '../../../../../../../shared/vulns/severity';
import { nuc } from '../../../../../../utils';
import IssueCountBadge from '../../../../../generic/Badge/IssueCountBadge';
import { RouterLink } from '../../../../../generic/Link/index';
import { isIssueOfActiveType } from '../helpers';
import type { Dependency, DependencyIssue } from '../../../../../../../shared/dependenciesV2';

import styles from './DetailsRow.module.less';

/**
 * Strip off the "/browse/dependencies" tail of the current dependencies page
 * URL and append the path to the project issues page for the given category.
 * If a single issue is present, link directly to that issue's full-page drawer
 * view. Otherwise, link to the issues page for the specific category and filter
 * the results down to a single dependency revision.
 */
const getFilteredIssuesPath = (
  currentPath: string,
  revision: string,
  issues: Array<Omit<DependencyIssue, 'notes'>>,
  category: string,
): string => {
  const parts = currentPath.split('/');
  const base = parts.slice(0, parts.length - 2).join('/');
  if (issues.length === 1 && issues[0]) {
    return `${base}/issues/${category}/${issues[0].id}`;
  }

  return `${base}/issues/${category}?filter[revisionIds][0]=${nuc(revision)}`;
};

const issueMapToCounts = (
  issueMap: Record<string, Array<Omit<DependencyIssue, 'notes'>>>,
) => _.mapValues(issueMap, issues => issues.length || 0);

type Props = {
  // Dependency data from API response
  dependency: Dependency;
};

/**
 * Displays issue and license details for a dependency
 */
const IssueSection: VoidFunctionComponent<Props> = ({ dependency }) => {
  const licensingIssues = dependency.issues.filter(issue => isIssueOfActiveType(issue, LICENSING_ISSUE_TYPES));
  const licensingIssuesByType = _.groupBy(
    licensingIssues,
    issue => issue.type,
  );
  const licensingCountByType = issueMapToCounts(licensingIssuesByType);

  const vulnIssues = dependency.issues.filter(issue => isIssueOfActiveType(issue, VULN_ISSUE_TYPES));
  const vulnIssuesByType = _.groupBy(
    vulnIssues,
    issue => vulnSeverityMappingToString(getSeverityFromCVSS(issue.cvssScore)),
  );
  const vulnCountByType = issueMapToCounts(vulnIssuesByType);

  const qualityIssues = dependency.issues.filter(issue => isIssueOfActiveType(issue, QUALITY_ISSUE_TYPES));
  const qualityIssuesByType = _.groupBy(
    qualityIssues,
    issue => issue.type,
  );
  const qualityCountByType = issueMapToCounts(qualityIssuesByType);

  return (
    <div className={styles.issueSection}>
      {!_.isEmpty(licensingCountByType) &&
        <RouterLink to={getFilteredIssuesPath(window.location.pathname, dependency.locator, licensingIssues, 'licensing')}>
          <IssueCountBadge
            id={`${dependency.locator}-licensing`}
            issueTypes={licensingCountByType}
            color="white"
            pluralLabel={(count: number) => plural(count, 'Licensing Issue')}
            fullWidth
          />
        </RouterLink>}
      {!_.isEmpty(vulnCountByType) &&
        <RouterLink to={getFilteredIssuesPath(window.location.pathname, dependency.locator, vulnIssues, 'vulnerability')}>
          <IssueCountBadge
            id={`${dependency.locator}-vulnerability`}
            issueTypes={vulnCountByType}
            color="white"
            pluralLabel={(count: number) => plural(count, 'Security Issue')}
            fullWidth
          />
        </RouterLink>}
      {!_.isEmpty(qualityCountByType) &&
        <RouterLink to={getFilteredIssuesPath(window.location.pathname, dependency.locator, qualityIssues, 'quality')}>
          <IssueCountBadge
            id={`${dependency.locator}-quality`}
            issueTypes={qualityCountByType}
            color="white"
            pluralLabel={(count: number) => plural(count, 'Quality Issue')}
            fullWidth
          />
        </RouterLink>}
    </div>
  );
};

export default IssueSection;
